/**
 * Phone matching for ingestion and OpenPhone webhooks.
 *
 * The RULES about what a phone number is — canonicalization, comparison digits,
 * match variants, `tel:` hrefs — now live in `@wtc/contact-identity/phone` and are
 * re-exported below so this stays the import path Hub already uses. What remains
 * here is the part that genuinely needs Hub: resolving those variants against the
 * `contacts` table.
 */

import type { SupabaseClient } from "@wtc/supabase";
import { phoneDigitsForComparison, phoneExactMatchVariants } from "@wtc/contact-identity/phone";
import { logger } from "../../logger";

export {
  normalizePhoneForInboundHub,
  isPlausiblePhoneNumber,
  telHrefForPhone,
  phoneDigitsForComparison,
  phoneExactMatchVariants,
} from "@wtc/contact-identity/phone";

export interface ContactRowPhoneLookup {
  id: string;
  name: string;
  email: string;
  email_aliases: string[] | null;
  phone: string | null;
  phone_aliases?: string[] | null;
  company_id: string | null;
  company_name: string | null;
  status: string | null;
}

/**
 * Resolve all contact rows matching a phone (primary or phone_aliases).
 * Used by OpenPhone webhooks when a shared company line maps to multiple people.
 */
export async function findContactRowsByPhoneMatch(
  supabase: SupabaseClient,
  rawPhone: string
): Promise<ContactRowPhoneLookup[]> {
  const { data: byDigits, error: digitsErr } = await supabase.rpc(
    "find_contacts_by_phone_digits",
    { p_raw_phone: rawPhone }
  );

  if (digitsErr) {
    logger.error("failed to query contacts by phone digits RPC (plural)", digitsErr, {
      op: "findContactRowsByPhoneMatch",
    });
  } else if (byDigits && byDigits.length > 0) {
    return byDigits;
  }

  const single = await findContactRowByPhoneMatchFallback(supabase, rawPhone);
  return single ? [single] : [];
}

/**
 * Resolve an existing contact row when `contacts.phone` matches common formatting variants
 * or shares the same digit-normalized key (last resort ilike on digits substring).
 */
export async function findContactRowByPhoneMatch(
  supabase: SupabaseClient,
  rawPhone: string
): Promise<ContactRowPhoneLookup | null> {
  const { data: byDigits, error: digitsErr } = await supabase.rpc(
    "find_contact_by_phone_digits",
    { p_raw_phone: rawPhone }
  );

  if (digitsErr) {
    logger.error("failed to query contacts by phone digits RPC", digitsErr, {
      op: "findContactRowByPhoneMatch",
    });
  } else {
    const rpcRow = byDigits?.[0];
    if (rpcRow) return rpcRow;
  }

  return findContactRowByPhoneMatchFallback(supabase, rawPhone);
}

async function findContactRowByPhoneMatchFallback(
  supabase: SupabaseClient,
  rawPhone: string
): Promise<ContactRowPhoneLookup | null> {
  const variants = phoneExactMatchVariants(rawPhone);
  if (variants.length === 0) return null;

  const { data: byVariants, error: variantErr } = await supabase
    .from("contacts")
    .select("id, name, email, email_aliases, phone, company_id, company_name, status")
    .in("phone", variants)
    .limit(1);

  if (variantErr) {
    logger.error("failed to query contacts by phone variants", variantErr, {
      op: "findContactRowByPhoneMatch",
    });
  }
  const firstVariant = byVariants?.[0];
  if (firstVariant) return firstVariant;

  const key = phoneDigitsForComparison(rawPhone);
  if (!key || key.length < 7) return null;

  const { data: candidates, error: looseErr } = await supabase
    .from("contacts")
    .select("id, name, email, email_aliases, phone, company_id, company_name, status")
    .not("phone", "is", null)
    .ilike("phone", `%${key}%`)
    .limit(25);

  if (looseErr) {
    logger.error("failed in fallback phone ilike query", looseErr, {
      op: "findContactRowByPhoneMatch",
    });
    return null;
  }

  const row = (candidates ?? []).find((c) => phoneDigitsForComparison(c.phone) === key);
  return row ?? null;
}
